export type WxbCalendarWeekStart = 0 | 1;

export interface WxbCalendarDateCell {
  key: string;
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

export interface BuildWxbCalendarMatrixOptions {
  viewDate: Date;
  selectedDate?: Date | null;
  today?: Date;
  weekStartsOn?: WxbCalendarWeekStart;
  minDate?: Date | null;
  maxDate?: Date | null;
  disabledDate?: (date: Date) => boolean;
}

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function padCalendarNumber(value: number): string {
  return value.toString().padStart(2, '0');
}

export function startOfCalendarDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function toCalendarDateKey(date: Date): string {
  return [
    date.getFullYear(),
    padCalendarNumber(date.getMonth() + 1),
    padCalendarNumber(date.getDate()),
  ].join('-');
}

export function parseCalendarDateKey(value?: string | null): Date | null {
  if (!value) return null;

  const match = DATE_KEY_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export function addCalendarDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

export function addCalendarMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

export function isSameCalendarDay(a?: Date | null, b?: Date | null): boolean {
  if (!a || !b) return false;
  return toCalendarDateKey(a) === toCalendarDateKey(b);
}

export function isDateBeforeCalendarDay(date: Date, boundary?: Date | null): boolean {
  if (!boundary) return false;
  return startOfCalendarDay(date).getTime() < startOfCalendarDay(boundary).getTime();
}

export function isDateAfterCalendarDay(date: Date, boundary?: Date | null): boolean {
  if (!boundary) return false;
  return startOfCalendarDay(date).getTime() > startOfCalendarDay(boundary).getTime();
}

export function getCalendarMonthTitle(date: Date): string {
  return `${date.getFullYear()}年${date.getMonth() + 1}月`;
}

export function buildWxbCalendarMatrix({
  viewDate,
  selectedDate = null,
  today = new Date(),
  weekStartsOn = 1,
  minDate = null,
  maxDate = null,
  disabledDate,
}: BuildWxbCalendarMatrixOptions): WxbCalendarDateCell[] {
  const normalizedViewDate = startOfCalendarDay(viewDate);
  const monthStart = new Date(normalizedViewDate.getFullYear(), normalizedViewDate.getMonth(), 1);
  const leadingDayCount = (monthStart.getDay() - weekStartsOn + 7) % 7;
  const gridStart = addCalendarDays(monthStart, -leadingDayCount);
  const normalizedToday = startOfCalendarDay(today);
  const normalizedSelectedDate = selectedDate ? startOfCalendarDay(selectedDate) : null;

  return Array.from({ length: 42 }, (_, index) => {
    const date = addCalendarDays(gridStart, index);
    const isDisabled = isDateBeforeCalendarDay(date, minDate)
      || isDateAfterCalendarDay(date, maxDate)
      || Boolean(disabledDate?.(date));

    return {
      key: toCalendarDateKey(date),
      date,
      dayOfMonth: date.getDate(),
      isCurrentMonth: date.getMonth() === monthStart.getMonth(),
      isToday: isSameCalendarDay(date, normalizedToday),
      isSelected: isSameCalendarDay(date, normalizedSelectedDate),
      isDisabled,
    };
  });
}
